import type { BadgeTone, IconName } from "@sagot/ui";
import type { AgentUpdate, AnswerLength, EvalRun, RetrievalConfigIn } from "../shared/types";

export const METRICS = ["faithfulness", "answer_correctness", "context_precision", "context_recall", "citation_accuracy", "refusal_correctness"] as const;
export type Metric = (typeof METRICS)[number];
/** Pass marker on the metric bars (backend PASS_THRESHOLD). */
export const PASS_THRESHOLD = 0.85;
/** Backend deltas are points (×100). */
const POINTS = 100;
const SAME_EPSILON = 0.005;
export const CSV_TEMPLATE = "question,expected_answer,expected_source\n\"What are your office hours?\",\"Monday to Friday, 8 AM to 5 PM.\",\"Office Guide.pdf\"\n";
export const CSV_MAX_BYTES = 512 * 1024;
export const ACTIVE_RUN = new Set(["queued", "running"]);
export const RUN_POLL_MS = 2000;

type Bag = Record<string, unknown>;

export function metricValue(run: Pick<EvalRun, "metrics">, metric: Metric): number | null {
  const value = (run.metrics)[metric];
  return typeof value === "number" ? value : null;
}

/** Mean of the six headline metrics that have a value. */
export function overallScore(run: Pick<EvalRun, "metrics">): number | null {
  const values = METRICS.map((m) => metricValue(run, m)).filter((v): v is number => v !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

/** "+0.02" / "−0.03" / "±0.00" from a points delta (or a raw score difference). */
export function formatDelta(delta: number | null | undefined, inPoints = true): string {
  if (delta === null || delta === undefined) return "";
  const value = inPoints ? delta / POINTS : delta;
  if (Math.abs(value) < SAME_EPSILON) return "±0.00";
  return `${value > 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}`;
}

export function deltaTone(delta: number | null | undefined): "up" | "down" | "same" {
  if (delta === null || delta === undefined || Math.abs(delta / POINTS) < SAME_EPSILON) return "same";
  return delta > 0 ? "up" : "down";
}

export function metricDelta(run: Pick<EvalRun, "deltas">, metric: Metric): number | null {
  const value = (run.deltas)[metric];
  return typeof value === "number" ? value : null;
}

export function totalCases(run: Pick<EvalRun, "pass_count" | "partial_count" | "fail_count">): number {
  return run.pass_count + run.partial_count + run.fail_count;
}

export interface HealthView {
  tone: BadgeTone;
  icon: IconName;
}

export function healthView(health: string): HealthView {
  if (health === "passing") return { tone: "success", icon: "check-circle" };
  if (health === "failing") return { tone: "danger", icon: "x-circle" };
  if (health === "stale") return { tone: "warning", icon: "alert-triangle" };
  return { tone: "neutral", icon: "circle" };
}

export interface ConfigView {
  model: string | null;
  chunkSize: number | null;
  reranker: string | null;
  topK: number | null;
  temperature: number | null;
}

export function configView(run: Pick<EvalRun, "config_snapshot">): ConfigView {
  const snapshot = run.config_snapshot;
  const rag = (typeof snapshot.rag_config === "object" && snapshot.rag_config !== null ? snapshot.rag_config : {}) as Bag;
  const routes = (typeof snapshot.routes === "object" && snapshot.routes !== null ? snapshot.routes : {}) as Bag;
  const chat = Array.isArray(routes["chat.default"]) ? (routes["chat.default"] as unknown[]) : [];
  const num = (v: unknown) => (typeof v === "number" ? v : null);
  return {
    model: typeof chat[0] === "string" ? chat[0] : null,
    chunkSize: num(rag.chunk_target_tokens),
    reranker: typeof rag.reranker === "string" ? rag.reranker : null,
    topK: num(rag.final_top_n),
    temperature: num(snapshot.temperature) ?? num(rag.temperature),
  };
}

const RETRIEVAL_BOUNDS: Readonly<Record<string, readonly [number, number]>> = {
  chunk_overlap_ratio: [0, 0.3], k_dense: [20, 100], k_lexical: [20, 100], k_fused: [20, 100], final_top_n: [3, 10],
  w_dense: [0, 2], w_lexical: [0, 2], rerank_min_score: [0, 0.8], max_chunks_per_source: [1, 10],
};
const RETRIEVAL_FLAGS = ["query_rewrite", "multi_query", "contextual_retrieval", "neighbor_expansion"] as const;
const CHUNK_SIZES = new Set([256, 512, 1024]);
const ANSWER_LENGTHS = new Set(["short", "medium", "detailed"]);

/** Snapshot → AgentUpdate: only fields the Agent Settings API accepts, inside its bounds. */
export function snapshotToUpdate(run: Pick<EvalRun, "config_snapshot">): AgentUpdate {
  const snapshot = run.config_snapshot;
  const rag = (typeof snapshot.rag_config === "object" && snapshot.rag_config !== null ? snapshot.rag_config : {}) as Bag;
  const retrieval: RetrievalConfigIn = {};
  for (const [key, [min, max]] of Object.entries(RETRIEVAL_BOUNDS)) {
    const value = rag[key];
    if (typeof value === "number" && value >= min && value <= max) Object.assign(retrieval, { [key]: value });
  }
  for (const key of RETRIEVAL_FLAGS) if (typeof rag[key] === "boolean") Object.assign(retrieval, { [key]: rag[key] });
  if (typeof rag.chunk_target_tokens === "number" && CHUNK_SIZES.has(rag.chunk_target_tokens)) retrieval.chunk_target_tokens = rag.chunk_target_tokens as 256 | 512 | 1024;
  const body: AgentUpdate = {};
  if (Object.keys(retrieval).length > 0) body.retrieval_config = retrieval;
  if (typeof snapshot.temperature === "number" && snapshot.temperature >= 0 && snapshot.temperature <= 1) body.temperature = snapshot.temperature;
  if (typeof snapshot.answer_length === "string" && ANSWER_LENGTHS.has(snapshot.answer_length)) body.answer_length = snapshot.answer_length as AnswerLength;
  return body;
}

/** Latest finished-or-running run across sets (by creation time). */
export function latestRun<T extends Pick<EvalRun, "created_at">>(runs: readonly T[]): T | undefined {
  return [...runs].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}

export function triggerType(run: Pick<EvalRun, "trigger">): string {
  const type = (run.trigger).type;
  return typeof type === "string" ? type : "manual";
}
